/** When each version of a review or email draft came in – the version picker, the header line and the inbox rows. */
import type { AdminReviewDoc, AdminReviewVersion } from "../api/admin";
import { humanizeDuration } from "./aiTeam";
import { ageSeconds, berlinDateTime } from "./console";

/** "29.10., 14:32 (5m ago)" – Berlin time and age; the server sends naive UTC, `berlinDateTime` reads it as such. */
export function whenText(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return "–";
  return `${berlinDateTime(iso)} (${humanizeDuration(ageSeconds(iso, nowMs))} ago)`;
}

/** One option of the version picker: "v2 · 28.10., 16:40", the current one "v3 · 29.10., 14:32 · current". */
export function versionOptionLabel(
  v: Pick<AdminReviewVersion, "content_version" | "submitted_at">,
  currentVersion: number,
): string {
  const parts = [`v${v.content_version}`];
  if (v.submitted_at) parts.push(berlinDateTime(v.submitted_at));
  if (v.content_version === currentVersion) parts.push("current");
  return parts.join(" · ");
}

/** The header line: when this version came in and, from v2 on, when the first one did –
 * "v3 submitted 29.10., 14:32 (5m ago) · v1 28.10., 09:12 (1d 5h ago)". */
export function submissionLine(
  doc: Pick<AdminReviewDoc, "content_version" | "submitted_at" | "created_at">,
  nowMs: number = Date.now(),
): string {
  const current = `v${doc.content_version} submitted ${whenText(doc.submitted_at, nowMs)}`;
  if (doc.content_version <= 1 || !doc.created_at) return current;
  return `${current} · v1 ${whenText(doc.created_at, nowMs)}`;
}

/** The tooltip of an inbox row's version and age: both submission times, the first one from v2 on. */
export function submissionTitle(doc: Pick<AdminReviewDoc, "content_version" | "submitted_at" | "created_at">): string {
  const current = `v${doc.content_version} submitted ${berlinDateTime(doc.submitted_at)}`;
  if (doc.content_version <= 1 || !doc.created_at) return current;
  return `${current} · v1 submitted ${berlinDateTime(doc.created_at)}`;
}
